import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type CarouselItem, type MediaAsset } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { MediaThumb } from "@/components/MediaThumb";
import PhotoPickerModal from "@/components/PhotoPickerModal";
import { useState } from "react";
import { cn } from "@/lib/utils";

function slide1HasMedia(item: CarouselItem | null | undefined): boolean {
  const rows = item?.slide_media || [];
  return rows.some((row) => {
    const idx = Number(row.slide_index ?? 0);
    return Boolean(row.asset_id) && (idx === 1 || idx === 0);
  });
}

function slide1Brief(item: CarouselItem) {
  const slides = item.copy?.slides || [];
  const first = (slides[0] || {}) as Record<string, unknown>;
  const parts = ["text", "heading", "sub", "body", "caption"]
    .map((key) => String(first[key] || "").trim())
    .filter(Boolean);
  if (!parts.length && item.title) parts.push(item.title);
  const photoKind =
    item.inspiration_borrow?.photo_kind && item.inspiration_analysis?.photo_kind
      ? String(item.inspiration_analysis.photo_kind)
      : "";
  return {
    pain_id: item.pain_id || "",
    tool_id: item.tool_id || "",
    book_id: item.book_id || "",
    funnel_stage: item.funnel_stage || "mofu",
    need_person: "yes",
    mode: "as_is",
    title: item.title || "",
    photo_kind: photoKind,
    copy_text: parts.join("\n"),
    limit: 3,
  };
}

export default function Slide1PhotoProposals({
  item,
  fallback = false,
}: {
  item: CarouselItem;
  fallback?: boolean;
}) {
  const qc = useQueryClient();
  const [pickerOpen, setPickerOpen] = useState(false);
  const brief = slide1Brief(item);
  const hasMedia = slide1HasMedia(item);

  const suggestQ = useQuery({
    queryKey: ["slide1-photo-proposals", item.id, brief.copy_text, brief.photo_kind, brief.funnel_stage],
    queryFn: () => api.mediaSuggest(brief),
    enabled: Boolean(item.id) && !hasMedia,
  });

  const attachM = useMutation({
    mutationFn: (assetId: string) =>
      api.carouselAttachMedia(item.id, 1, { asset_id: assetId, mode: "as_is" }, fallback, "one"),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["carousel", item.id] });
      qc.invalidateQueries({ queryKey: ["carousels"] });
      qc.invalidateQueries({ queryKey: ["slide1-photo-proposals"] });
      setPickerOpen(false);
    },
  });

  if (hasMedia) return null;

  const items = (suggestQ.data?.items || []).slice(0, 3);

  return (
    <div className="rounded-md border border-border bg-muted/20 p-3">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm font-medium">Propozycje na slajd 1</p>
          <p className="text-[11px] text-muted-foreground">
            Trzy najbardziej adekwatne zdjęcia z biblioteki. Kliknij, żeby podpiąć (bez auto-attach).
          </p>
        </div>
        <Button type="button" size="sm" variant="outline" onClick={() => setPickerOpen(true)}>
          Więcej w bibliotece
        </Button>
      </div>
      {suggestQ.isLoading ? (
        <p className="text-xs text-muted-foreground">Szukam zdjęć…</p>
      ) : null}
      {suggestQ.isError ? (
        <p className="text-xs text-destructive">
          {(suggestQ.error as Error)?.message || "Nie udało się pobrać rekomendacji"}
        </p>
      ) : null}
      {!suggestQ.isLoading && !items.length ? (
        <p className="text-xs text-muted-foreground">
          Brak rekomendacji powyżej progu. Otwórz bibliotekę albo dodaj zdjęcia w Media.
        </p>
      ) : null}
      {items.length ? (
        <div className="grid grid-cols-3 gap-2">
          {items.map((asset: MediaAsset) => (
            <button
              key={asset.id}
              type="button"
              disabled={attachM.isPending}
              className={cn(
                "overflow-hidden rounded-md border border-border bg-background text-left transition hover:border-brand",
                attachM.isPending && attachM.variables === asset.id && "opacity-60",
              )}
              onClick={() => attachM.mutate(asset.id)}
            >
              <MediaThumb asset={asset} />
              <p className="truncate px-1.5 py-1 text-[11px]">{asset.original_name || asset.id.slice(0, 8)}</p>
              {typeof asset.score === "number" ? (
                <p className="px-1.5 pb-1 text-[10px] text-muted-foreground">score {asset.score}</p>
              ) : null}
            </button>
          ))}
        </div>
      ) : null}
      {attachM.isError ? (
        <p className="mt-2 text-xs text-destructive">
          {(attachM.error as Error)?.message || "Nie udało się podpiąć zdjęcia"}
        </p>
      ) : null}
      {pickerOpen ? (
        <PhotoPickerModal
          brief={brief}
          onPick={(asset) => attachM.mutate(asset.id)}
          onClose={() => setPickerOpen(false)}
        />
      ) : null}
    </div>
  );
}
